import React, { useEffect, useState, useRef } from "react";
import { Button, Row, Modal, Form, Card, InputGroup } from "react-bootstrap";
import axios from "axios";
import { jsPDF } from 'jspdf';
import { useNotification } from "../../../context/NotificationContext";
import SurveyList from "./feedback-components/SurveyList";
import SurveyEditor from "./feedback-components/SurveyEditor";
import templates from "./FeedbackTemplates"; // Adjust the path as needed
import { generateSurveyLink } from "./feedback-components/config";

// ─── Design tokens (shared across library + preview) ─────────────────────────
const T = {
  brand:   "#5b5bd6",
  brandLt: "#ede9fe",
  text:    "#111827",
  muted:   "#6b7280",
  subtle:  "#9ca3af",
  border:  "#e5e7eb",
  bg:      "#f9fafb",
  white:   "#ffffff",
  danger:  "#ef4444",
};

// ─── Category filter config ───────────────────────────────────────────────────
const CATEGORIES = [
  { key: "all",      label: "All Templates", icon: "fa-th-large"      },
  { key: "form",     label: "Data Forms",    icon: "fa-wpforms"       },
  { key: "feedback", label: "Feedback",      icon: "fa-star"          },
  { key: "blank",    label: "Start Blank",   icon: "fa-file-medical"  },
];

// ─── Field type preview renderer ─────────────────────────────────────────────
const PreviewField = ({ q, themeColor = "#5b5bd6" }) => {
  const inp = {
    width: "100%", padding: "8px 11px", fontSize: "0.84rem",
    border: "1px solid #e5e7eb", borderRadius: 8,
    background: "#fff", color: "#9ca3af", outline: "none",
    boxSizing: "border-box", fontFamily: "inherit",
  };

  if (q.type === "section_header") {
    return (
      <div style={{ fontWeight: 700, fontSize: "0.94rem", color: themeColor,
        borderBottom: `2px solid ${themeColor}30`, paddingBottom: 4, marginBottom: 2 }}>
        {q.label}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: "0.79rem", fontWeight: 600, color: T.text, marginBottom: 5 }}>
        {q.label}
        {q.required && <span style={{ color: T.danger, marginLeft: 3 }}>*</span>}
      </div>
      {(q.type === "text" || q.type === "email" || q.type === "phone" || q.type === "number") && (
        <input disabled placeholder={q.placeholder || `Enter ${q.label?.toLowerCase() || "value"}…`} style={inp} />
      )}
      {q.type === "textarea" && (
        <textarea disabled rows={2} placeholder={q.placeholder || "Type here…"}
          style={{ ...inp, resize: "none" }} />
      )}
      {q.type === "date" && (
        <input type="date" disabled style={inp} />
      )}
      {q.type === "dropdown" && (
        <select disabled style={{ ...inp, appearance: "none" }}>
          <option>{(q.options?.[0]) || "Select an option…"}</option>
          {(q.options || []).slice(1, 3).map((o, i) => <option key={i}>{o}</option>)}
        </select>
      )}
      {q.type === "radio" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {(q.options || ["Option A", "Option B"]).slice(0, 3).map((o, i) => (
            <label key={i} style={{ display: "flex", alignItems: "center", gap: 7,
              fontSize: "0.81rem", color: T.muted }}>
              <input type="radio" disabled style={{ accentColor: themeColor }} /> {o}
            </label>
          ))}
        </div>
      )}
      {q.type === "checkbox" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {(q.options || ["Option A"]).slice(0, 2).map((o, i) => (
            <label key={i} style={{ display: "flex", alignItems: "center", gap: 7,
              fontSize: "0.81rem", color: T.muted }}>
              <input type="checkbox" disabled style={{ accentColor: themeColor }} /> {o}
            </label>
          ))}
        </div>
      )}
      {q.type === "rating" && (
        <div style={{ display: "flex", gap: 4 }}>
          {[1,2,3,4,5].map(n => (
            <span key={n} style={{ fontSize: "1.3rem" }}>
              {q.style === "smiley"
                ? ["😞","😟","😐","🙂","😄"][n-1]
                : "⭐"}
            </span>
          ))}
        </div>
      )}
      {q.type === "nps" && (
        <div style={{ display: "flex", gap: 3 }}>
          {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
            <div key={n} style={{
              width: 28, height: 28, borderRadius: 5, fontSize: "0.7rem",
              fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center",
              border: "1px solid #e5e7eb",
              background: n === 8 ? themeColor : "#fff",
              color: n === 8 ? "#fff" : T.muted,
            }}>{n}</div>
          ))}
        </div>
      )}
      {q.type === "multiple" && (
        <select disabled style={{ ...inp, appearance: "none" }}>
          <option>Select…</option>
        </select>
      )}
    </div>
  );
};

// ─── TemplateLibrary — full-height right-panel modal content ─────────────────
const TemplateLibrary = ({ templates, onUse, onBlank, onPreview, onClose }) => {
  const [search,   setSearch]   = useState("");
  const [category, setCategory] = useState("all");

  const allWithBlank = [...templates, { name: "Start Blank", category: "blank", icon: "fa-file-medical", color: T.brand, description: "Build your form from scratch — full control over every field and setting.", questions: [] }];

  const filtered = allWithBlank.filter(t => {
    const matchCat = category === "all" || t.category === category;
    const matchQ   = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.description?.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchQ;
  });

  const totalCount = templates.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: T.white }}>

      {/* ── Header ── */}
      <div style={{ padding: "20px 24px 0", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: "1.3rem", fontWeight: 800, color: T.text,
              letterSpacing: "-0.02em", marginBottom: 3 }}>
              Template Library
            </div>
            <div style={{ fontSize: "0.81rem", color: T.muted }}>
              {totalCount} ready-to-use templates · click to preview, then use
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* New Form from Scratch CTA */}
            <button onClick={onBlank}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                padding: "9px 18px", borderRadius: 10, border: "none",
                background: T.brand, color: T.white,
                fontSize: "0.84rem", fontWeight: 700, cursor: "pointer",
                transition: "opacity 0.12s",
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
              <i className="fas fa-plus" style={{ fontSize: "0.74rem" }} />
              New Blank Form
            </button>
            <button onClick={onClose}
              style={{ border: "none", background: "none", color: T.subtle,
                cursor: "pointer", fontSize: "1.3rem", lineHeight: 1, padding: "4px 6px" }}
              onMouseEnter={e => { e.currentTarget.style.color = T.danger; }}
              onMouseLeave={e => { e.currentTarget.style.color = T.subtle; }}>
              ×
            </button>
          </div>
        </div>

        {/* Search */}
        <div style={{ position: "relative", marginBottom: 14 }}>
          <i className="fas fa-search" style={{
            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            color: T.subtle, fontSize: "0.8rem",
          }} />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search templates by name or keyword…"
            style={{
              width: "100%", padding: "10px 12px 10px 36px", fontSize: "0.84rem",
              border: `1px solid ${T.border}`, borderRadius: 10, outline: "none",
              boxSizing: "border-box", background: T.bg, color: T.text,
            }}
            onFocus={e => { e.target.style.borderColor = T.brand; e.target.style.boxShadow = `0 0 0 3px ${T.brandLt}`; e.target.style.background = T.white; }}
            onBlur={e  => { e.target.style.borderColor = T.border; e.target.style.boxShadow = "none"; e.target.style.background = T.bg; }}
          />
          {search && (
            <button onClick={() => setSearch("")}
              style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                border: "none", background: "none", color: T.subtle, cursor: "pointer", fontSize: "1rem" }}>
              ×
            </button>
          )}
        </div>

        {/* Category filter pills */}
        <div style={{ display: "flex", gap: 6, paddingBottom: 16,
          borderBottom: `1px solid ${T.border}`, flexWrap: "wrap" }}>
          {CATEGORIES.map(cat => {
            const active = category === cat.key;
            const count  = cat.key === "all"
              ? templates.length
              : cat.key === "blank" ? 1
              : templates.filter(t => t.category === cat.key).length;
            return (
              <button key={cat.key} onClick={() => setCategory(cat.key)}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 5,
                  padding: "6px 13px", borderRadius: 20, cursor: "pointer",
                  border: `1.5px solid ${active ? T.brand : T.border}`,
                  background: active ? T.brand : T.white,
                  color:      active ? T.white : T.muted,
                  fontSize: "0.78rem", fontWeight: 600, transition: "all 0.12s",
                }}>
                <i className={`fas ${cat.icon}`} style={{ fontSize: "0.68rem" }} />
                {cat.label}
                <span style={{
                  fontSize: "0.62rem", fontWeight: 700, minWidth: 16, textAlign: "center",
                  padding: "1px 5px", borderRadius: 10,
                  background: active ? "rgba(255,255,255,0.25)" : T.bg,
                  color: active ? T.white : T.subtle,
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Card grid ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "18px 24px 24px" }}>

        {filtered.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <i className="fas fa-search" style={{ fontSize: "2rem", color: T.border, display: "block", marginBottom: 12 }} />
            <div style={{ fontSize: "0.94rem", fontWeight: 600, color: T.subtle }}>No templates match "{search}"</div>
            <button onClick={() => { setSearch(""); setCategory("all"); }}
              style={{ marginTop: 10, border: "none", background: "none",
                color: T.brand, fontSize: "0.82rem", cursor: "pointer", fontWeight: 600 }}>
              Clear filters
            </button>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
          {filtered.map((t, i) => {
            const isBlank = t.category === "blank";
            return (
              <div key={i}
                onClick={() => isBlank ? onBlank() : onPreview(t)}
                style={{
                  background: T.white, borderRadius: 12,
                  border: isBlank ? `2px dashed ${T.brand}40` : `1px solid ${T.border}`,
                  borderTop: isBlank ? `2px dashed ${T.brand}40` : `3px solid ${t.color || T.brand}`,
                  padding: "16px 16px 14px", cursor: "pointer",
                  display: "flex", flexDirection: "column", gap: 0,
                  transition: "all 0.15s",
                  position: "relative",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.boxShadow = `0 6px 24px rgba(0,0,0,0.09)`;
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.boxShadow = "none";
                  e.currentTarget.style.transform = "none";
                }}>

                {/* Category badge top-right */}
                <span style={{
                  position: "absolute", top: 12, right: 12,
                  fontSize: "0.58rem", fontWeight: 800, textTransform: "uppercase",
                  letterSpacing: "0.06em", padding: "2px 7px", borderRadius: 20,
                  background: `${t.color || T.brand}15`,
                  color: t.color || T.brand,
                }}>
                  {isBlank ? "Blank" : t.category === "form" ? "Form" : "Survey"}
                </span>

                {/* Icon + name */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                    background: `${t.color || T.brand}15`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {isBlank
                      ? <i className="fas fa-plus" style={{ color: T.brand, fontSize: "0.9rem" }} />
                      : <i className={`fas ${t.icon || "fa-file-alt"}`} style={{ color: t.color || T.brand, fontSize: "0.9rem" }} />
                    }
                  </div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 700, color: T.text,
                    lineHeight: 1.3, paddingRight: 36 }}>
                    {t.name}
                  </div>
                </div>

                {/* Description */}
                <div style={{ fontSize: "0.77rem", color: T.muted, lineHeight: 1.55,
                  flex: 1, marginBottom: 14, minHeight: 52 }}>
                  {t.description}
                </div>

                {/* Footer: field count + action buttons */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                  paddingTop: 10, borderTop: `1px solid ${T.bg}` }}>
                  {!isBlank ? (
                    <span style={{ fontSize: "0.71rem", color: T.subtle, fontWeight: 500 }}>
                      <i className="fas fa-list-ul" style={{ marginRight: 4, fontSize: "0.62rem" }} />
                      {t.questions.length} field{t.questions.length !== 1 ? "s" : ""}
                    </span>
                  ) : <span />}
                  <div style={{ display: "flex", gap: 6 }}
                    onClick={e => e.stopPropagation()}>
                    {!isBlank && (
                      <button onClick={() => onPreview(t)}
                        style={{
                          padding: "5px 12px", borderRadius: 7, cursor: "pointer",
                          border: `1px solid ${T.border}`, background: T.white,
                          color: T.muted, fontSize: "0.76rem", fontWeight: 600,
                          display: "inline-flex", alignItems: "center", gap: 5,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = T.brand; e.currentTarget.style.color = T.brand; }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.muted; }}>
                        <i className="fas fa-eye" style={{ fontSize: "0.68rem" }} /> Preview
                      </button>
                    )}
                    <button onClick={() => isBlank ? onBlank() : onUse(t)}
                      style={{
                        padding: "5px 14px", borderRadius: 7, cursor: "pointer",
                        border: "none",
                        background: t.color || T.brand,
                        color: T.white, fontSize: "0.76rem", fontWeight: 700,
                        display: "inline-flex", alignItems: "center", gap: 5,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.opacity = "0.85"; }}
                      onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                      <i className="fas fa-arrow-right" style={{ fontSize: "0.65rem" }} />
                      {isBlank ? "Start" : "Use"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── TemplatePreview — full-height right-panel with live form mockup ──────────
const TemplatePreview = ({ template, onUse, onClose }) => {
  const color = template.branding?.themeColor || template.color || T.brand;
  const companyLogo = typeof localStorage !== "undefined" ? localStorage.getItem("companylogo") : null;

  // Separate section headers from actual fields for count
  const fieldCount = template.questions.filter(q => q.type !== "section_header").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: T.white }}>

      {/* ── Preview header ── */}
      <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {/* Back chevron */}
            <button onClick={onClose}
              style={{ border: `1px solid ${T.border}`, background: T.white, borderRadius: 8,
                width: 34, height: 34, cursor: "pointer", display: "flex",
                alignItems: "center", justifyContent: "center", color: T.muted, flexShrink: 0 }}>
              <i className="fas fa-arrow-left" style={{ fontSize: "0.78rem" }} />
            </button>
            <div>
              <div style={{ fontSize: "1.1rem", fontWeight: 800, color: T.text,
                letterSpacing: "-0.015em" }}>
                {template.name}
              </div>
              <div style={{ fontSize: "0.76rem", color: T.subtle, marginTop: 1 }}>
                {fieldCount} fields · {template.category === "form" ? "Data Collection" : "Feedback Survey"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button onClick={onUse}
              style={{
                display: "inline-flex", alignItems: "center", gap: 7,
                padding: "9px 20px", borderRadius: 10, border: "none",
                background: color, color: T.white,
                fontSize: "0.84rem", fontWeight: 700, cursor: "pointer",
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
              <i className="fas fa-check" style={{ fontSize: "0.74rem" }} />
              Use This Template
            </button>
            <button onClick={onClose}
              style={{ border: "none", background: "none", color: T.subtle,
                cursor: "pointer", fontSize: "1.3rem", lineHeight: 1, padding: "4px 6px" }}
              onMouseEnter={e => { e.currentTarget.style.color = T.danger; }}
              onMouseLeave={e => { e.currentTarget.style.color = T.subtle; }}>
              ×
            </button>
          </div>
        </div>
      </div>

      {/* ── Two-col: metadata left, form preview right ── */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", gap: 0 }}>

        {/* Left: template metadata */}
        <div style={{ width: 200, flexShrink: 0, padding: "20px 18px",
          borderRight: `1px solid ${T.border}`, background: T.bg }}>

          <div style={{ width: 44, height: 44, borderRadius: 12, marginBottom: 12,
            background: `${color}18`,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
            <i className={`fas ${template.icon || "fa-file-alt"}`}
              style={{ color, fontSize: "1.1rem" }} />
          </div>

          <div style={{ fontSize: "0.68rem", fontWeight: 800, color: T.subtle,
            textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>
            Description
          </div>
          <div style={{ fontSize: "0.79rem", color: T.muted, lineHeight: 1.6, marginBottom: 18 }}>
            {template.description}
          </div>

          <div style={{ fontSize: "0.68rem", fontWeight: 800, color: T.subtle,
            textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>
            Fields included
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {template.questions.filter(q => q.type !== "section_header").map((q, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 5, height: 5, borderRadius: "50%",
                  background: color, flexShrink: 0 }} />
                <span style={{ fontSize: "0.76rem", color: T.muted,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {q.label || q.type}
                </span>
                {q.required && (
                  <span style={{ fontSize: "0.55rem", color: T.danger, fontWeight: 700 }}>REQ</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right: live form mock */}
        <div style={{ flex: 1, padding: "24px 24px 40px", overflowY: "auto" }}>
          <div style={{ maxWidth: 480, margin: "0 auto" }}>

            {/* Form header mock */}
            <div style={{
              padding: "24px 24px 20px", borderRadius: 16, marginBottom: 20,
              background: template.branding?.backgroundValue || "#ffffff",
              border: `1px solid ${T.border}`,
            }}>
              {/* Logo */}
              {template.branding?.showLogo && companyLogo && (
                <img src={companyLogo} alt="Logo"
                  style={{ height: 36, display: "block", margin: "0 auto 16px",
                    objectFit: "contain", borderRadius: 6 }} />
              )}
              {/* Form title */}
              <div style={{
                fontSize: `${template.branding?.titleFontSize || 1.6}rem`,
                fontWeight: 800, color: template.branding?.titleColor || T.text,
                textAlign: "center", lineHeight: 1.3, marginBottom: 6,
                letterSpacing: "-0.02em",
              }}>
                {template.title || template.name}
              </div>
              {/* Description */}
              {template.description && (
                <div style={{ fontSize: "0.82rem", color: T.muted, textAlign: "center",
                  lineHeight: 1.55, maxWidth: 360, margin: "0 auto" }}>
                  {template.description.split(" ").slice(0, 18).join(" ")}
                  {template.description.split(" ").length > 18 ? "…" : ""}
                </div>
              )}
            </div>

            {/* Fields */}
            <div style={{ padding: "0 4px" }}>
              {template.questions.map((q, i) => (
                <PreviewField key={i} q={q} themeColor={color} />
              ))}
            </div>

            {/* Submit button mock */}
            <button disabled
              style={{
                width: "100%", padding: "12px 0", marginTop: 8,
                borderRadius: template.branding?.buttonStyle === "square" ? 6 : 999,
                border: "none", background: color, color: T.white,
                fontSize: "0.9rem", fontWeight: 700, cursor: "not-allowed",
                letterSpacing: "0.03em",
              }}>
              Submit
            </button>

            {/* Use template CTA at bottom */}
            <div style={{ marginTop: 24, padding: "16px", borderRadius: 12,
              background: `${color}0d`, border: `1px solid ${color}25`,
              textAlign: "center" }}>
              <div style={{ fontSize: "0.82rem", color: T.muted, marginBottom: 10 }}>
                Happy with this template?
              </div>
              <button onClick={onUse}
                style={{ padding: "9px 28px", borderRadius: 9, border: "none",
                  background: color, color: T.white,
                  fontSize: "0.84rem", fontWeight: 700, cursor: "pointer",
                  display: "inline-flex", alignItems: "center", gap: 7 }}
                onMouseEnter={e => { e.currentTarget.style.opacity = "0.88"; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                <i className="fas fa-bolt" style={{ fontSize: "0.72rem" }} />
                Use Template &amp; Open Editor
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ManageSurveys = () => {
  const [surveys, setSurveys] = useState([]);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [questions, setQuestions] = useState([]);
  // Branding (without conditionalRedirect)
  const [branding, setBranding] = useState({
    themeColor: "#ff4100",
    titleColor: "#000000",
    titleFontSize: 2,
    showLogo: false,
    logoSrc: "",
    buttonStyle: "rounded",
    background: "solid",
    backgroundValue: "#ffffff",
    thankYou: "Thank you for your feedback!",
    redirectUrl: "", // keep plain redirect if needed
  });
  const [settings, setSettings] = useState({
    anonymous: true,
    expiry: "",
    notifications: false,
    recipients: "",
    access: "public",
    language: "en",
  });
  const [globalSettings, setGlobalSettings] = useState({
    holder_logo: "",
    holder_caption: "",
    holder_desc: "",
    form_caption: "",
    form_desc: "",
    show_logo: true,
    show_phone: true,
    show_email: true,
    show_birthday: true,
    show_anniversary: true,
    show_suggestion: true,
    overall_exp: true,
    form_title: "",
    form_desc_long: "",
    thank_you_msg: "",
  });
  const [qrCodeSettings, setQrCodeSettings] = useState({
    enabled: false,
    size: 128,
    fgColor: "#000000",
    bgColor: "#ffffff",
    showLogo: true,
    logoSize: 0.2,
    qrTitle: "",
    qrTitleColor: "#000000",
    qrTitleSize: 24,
    qrDesc: "",
    qrDescColor: "#000000",
    qrDescSize: 16,
    headerLogo: "",
    borderGradientStart: "#007bff",
    borderGradientEnd: "#66b2ff",
    showHolderLogo: false,
    headerLogoWidth: 200,
    headerLogoHeight: 80,
    pageSize: '4-6',

  });
  // Move conditionalRedirect here
  const [conditionalLogic, setConditionalLogic] = useState({
    enabled: false,
    thresholdQuestionId: null,
    threshold: 7,
    goodUrl: "",
    badUrl: "",
    goodMessage: "Thank you! Please rate us here.",
    badMessage: "Thank you for your feedback!",
  });
  const [surveyLink, setSurveyLink] = useState("");
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [activeKey, setActiveKey] = useState("details");
  const initialConfigRef = useRef(null);
  const { notify } = useNotification();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000/api/pos";
  const token = localStorage.getItem("token");

    const companyLogo = localStorage.getItem("companylogo");
  const cdnUrl = process.env.REACT_APP_CDN_URL;
const logoNetShine = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : 'https://netshine-assets.blr1.digitaloceanspaces.com/company_87/logos/company_logo_87_1739613280498';
  const defaultLogoSrc = companyLogo && companyLogo.trim() ? `${cdnUrl}/${companyLogo}` : logoNetShine;

  const authHeaders = () => ({
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
  });
  useEffect(() => {
    fetchSurveys();
    fetchGlobalSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Fetch surveys
  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/feedback/surveys`, authHeaders());
      const data = Array.isArray(res.data) ? res.data : [];
      const normalized = data.map((s) => ({
        id: s.surveyid || s.id || `temp-${Date.now()}-${Math.random()}`,
        surveyid: s.surveyid || s.id || `temp-${Date.now()}-${Math.random()}`,
        title: s.title || "Untitled Survey",
        key: (s.sharing && s.sharing.key) || s.key || `survey-${s.surveyid || s.id || Date.now()}`,
        createdAt: s.created_at
          ? new Date(s.created_at).toISOString().split("T")[0]
          : s.createdAt || new Date().toISOString().split("T")[0],
        status: s.status || "Draft",
        responses: s.total_responses || s.responses || 0,
        questions: s.questions || [],
        branding: s.branding || {},
        settings: s.settings || {},
        qrCodeSettings: s.qrcode_settings || {},
        conditionalLogic: s.conditional_logic || {},
        description: s.description || "",
      }));
      setSurveys(normalized);
    } catch (err) {
      console.error("Error fetching surveys:", err);
      notify("error", "Failed to load surveys.");
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };
  const fetchGlobalSettings = async () => {
    try {
      const res = await axios.get(`${apiUrl}/feedback/settings`, authHeaders());
      if (res.data) setGlobalSettings((prev) => ({ ...prev, ...res.data }));
    } catch (err) {
      console.warn("Could not fetch global settings:", err?.response?.status || err);
    }
  };
  // Load a survey for editing
  const loadSurveyFromApi = async (surveyid) => {
    try {
      setLoading(true);
      const res = await axios.get(`${apiUrl}/feedback/surveys/${surveyid}`, authHeaders());
      const s = res.data;
      const normalized = {
        id: s.surveyid || s.id || `temp-${Date.now()}-${Math.random()}`,
        surveyid: s.surveyid || s.id || `temp-${Date.now()}-${Math.random()}`,
        title: s.title || "Untitled Survey",
        key: (s.sharing && s.sharing.key) || s.key || `survey-${s.surveyid || s.id || Date.now()}`,
        createdAt: s.created_at
          ? new Date(s.created_at).toISOString().split("T")[0]
          : s.createdAt || new Date().toISOString().split("T")[0],
        status: s.status || "Draft",
        responses: s._response_count || s.responses || 0,
        questions: s.questions || [],
        branding: s.branding || {},
        settings: s.settings || {},
        qrCodeSettings: s.qrcode_settings || {},
        conditionalLogic: s.conditional_logic || {},
        description: s.description || "",
      };
      setEditingSurvey(normalized);
      setQuestions(normalized.questions || []);
      setBranding({ ...branding, ...(normalized.branding || {}) });
      setSettings({ ...settings, ...(normalized.settings || {}) });
      setQrCodeSettings({ ...qrCodeSettings, ...(normalized.qrCodeSettings || {}) });
      setConditionalLogic({ ...conditionalLogic, ...(normalized.conditionalLogic || {}) });
      updateSharing(normalized);
      initialConfigRef.current = JSON.stringify(normalized);
    } catch (err) {
      console.error("Error loading survey:", err);
      notify("error", "Failed to load survey details.");
    } finally {
      setLoading(false);
    }
  };
  const startEditing = async (survey = null) => {
    if (survey) {
      if (survey.surveyid) {
        await loadSurveyFromApi(survey.surveyid);
      } else {
        setEditingSurvey(survey);
        setQuestions(survey.questions || []);
        setBranding(survey.branding || branding);
        setSettings(survey.settings || settings);
        setQrCodeSettings(survey.qrCodeSettings || qrCodeSettings);
        setConditionalLogic(survey.conditionalLogic || conditionalLogic);
        updateSharing(survey);
        initialConfigRef.current = JSON.stringify(survey);
      }
    } else setShowTemplateModal(true);
    setActiveKey("details");
  };
  const loadTemplate = (template) => {
    const newId = (surveys.length || 0) + 1;
    const newKey = `survey-${newId}`;
    const newSurvey = {
      id: newId,
      title: template.title || "Untitled Survey",
      key: newKey,
      createdAt: new Date().toISOString().split("T")[0],
      status: "Draft",
      responses: 0,
      questions: template.questions || [],
      branding: template.branding || branding,
      settings: template.settings || settings,
      qrCodeSettings: template.qrCodeSettings || qrCodeSettings,
      conditionalLogic: template.conditionalLogic || {},
    };
    setEditingSurvey(newSurvey);
    setQuestions(newSurvey.questions);
    setBranding(newSurvey.branding);
    setSettings(newSurvey.settings);
    setQrCodeSettings(newSurvey.qrCodeSettings);
    setConditionalLogic(newSurvey.conditionalLogic);
    updateSharing(newSurvey);
    setShowTemplateModal(false);
    initialConfigRef.current = JSON.stringify(newSurvey);
  };
  const loadBlank = () => {
    const newId = (surveys.length || 0) + 1;
    const newKey = `survey-${newId}`;
    const newSurvey = {
      id: newId,
      title: "",
      key: newKey,
      createdAt: new Date().toISOString().split("T")[0],
      status: "Draft",
      responses: 0,
      questions: [],
      branding: branding,
      settings: settings,
      qrCodeSettings: qrCodeSettings,
      conditionalLogic: {},
    };
    setEditingSurvey(newSurvey);
    setQuestions(newSurvey.questions);
    setBranding(newSurvey.branding);
    setSettings(newSurvey.settings);
    setQrCodeSettings(newSurvey.qrCodeSettings);
    setConditionalLogic(newSurvey.conditionalLogic);
    updateSharing(newSurvey);
    setShowTemplateModal(false);
    initialConfigRef.current = JSON.stringify(newSurvey);
  };
  const stopEditing = () => {
    setEditingSurvey(null);
    setQuestions([]);
    setIsDownloadOpen(false);
    setActiveKey("details");
  };
  const updateSharing = (survey) => {
    const link = generateSurveyLink(survey.surveyid || survey.id);
    setSurveyLink(link);
  };
  const generateQRCanvas = (callback) => {
    const svg = document.getElementById("qr-code");
    if (!svg) {
      console.error("QR code SVG not found");
      return;
    }
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src =
      "data:image/svg+xml;base64," +
      window.btoa(unescape(encodeURIComponent(svgData)));
    img.onload = () => {
      // Target resolution for 6×6 cm @ 300dpi = ~708px
      const targetPx = Math.round((6 * 300) / 2.54); // cm → px
      const canvas = document.createElement("canvas");
      canvas.width = targetPx;
      canvas.height = targetPx;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      callback(canvas);
    };
    img.onerror = () => {
      console.error("Failed to load QR SVG for rasterization");
    };
  };
  function renderQRWithBorder(
    pdf,
    qrDataURL,
    qrX,
    qrY,
    qrCm,
    options = {}
  ) {
    const {
      borderThicknessCm = 0.5, // thickness in cm
      gradientStart = "#007bff", // dark blue
      gradientEnd = "#66b2ff",   // light blue
    } = options;
    const borderPx = 800; // high-res canvas
    const borderCanvas = document.createElement("canvas");
    borderCanvas.width = borderPx;
    borderCanvas.height = borderPx;
    const ctx = borderCanvas.getContext("2d");
    // Gradient fill
    const gradient = ctx.createLinearGradient(0, 0, borderPx, borderPx);
    gradient.addColorStop(0, gradientStart);
    gradient.addColorStop(1, gradientEnd);
    // Outer rounded rect
    const radius = 60;
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.moveTo(radius, 0);
    ctx.lineTo(borderPx - radius, 0);
    ctx.quadraticCurveTo(borderPx, 0, borderPx, radius);
    ctx.lineTo(borderPx, borderPx - radius);
    ctx.quadraticCurveTo(borderPx, borderPx, borderPx - radius, borderPx);
    ctx.lineTo(radius, borderPx);
    ctx.quadraticCurveTo(0, borderPx, 0, borderPx - radius);
    ctx.lineTo(0, radius);
    ctx.quadraticCurveTo(0, 0, radius, 0);
    ctx.closePath();
    ctx.fill();
    // White inner box
    const innerMargin = (borderThicknessCm / qrCm) * borderPx;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(
      innerMargin,
      innerMargin,
      borderPx - innerMargin * 2,
      borderPx - innerMargin * 2
    );
    // Convert to image
    const borderDataURL = borderCanvas.toDataURL("image/png");
    // Draw gradient border box
    pdf.addImage(
      borderDataURL,
      "PNG",
      qrX - borderThicknessCm,
      qrY - borderThicknessCm,
      qrCm + borderThicknessCm * 2,
      qrCm + borderThicknessCm * 2
    );
    // Draw QR inside
    pdf.addImage(qrDataURL, "PNG", qrX, qrY, qrCm, qrCm);
  }


const downloadQRPDF = (
  qrTitle,
  qrTitleColor,
  qrTitleSize,
  qrDesc,
  qrDescColor,
  qrDescSize,
  headerLogoWidth,
  headerLogoHeight
) => {
  const pageSize = qrCodeSettings.pageSize || "4-6";
  const [widthIn, heightIn] = pageSize.split("-").map(Number);

  const baseWidthIn = 4;
  const baseHeightIn = 6;
  const scale = Math.min(widthIn / baseWidthIn, heightIn / baseHeightIn);

  const pageW = widthIn * 2.54; // cm
  const pageH = heightIn * 2.54; // cm
  const dpi = 300;
  const pxToCm = 2.54 / 96;

  // Safe margin inside border
  const safeMargin = qrCodeSettings.borderless ? 0 : 0.25; // cm

  // Footer strip height
  const footerH = 2.5 * scale;

  // --- Gradient border background ---
  const borderCanvas = document.createElement("canvas");
  borderCanvas.width = Math.round(widthIn * dpi);
  borderCanvas.height = Math.round(heightIn * dpi);
  const ctx = borderCanvas.getContext("2d");

  const gradient = ctx.createLinearGradient(0, 0, borderCanvas.width, borderCanvas.height);
  gradient.addColorStop(0, qrCodeSettings.borderGradientStart || "#2d2e83");
  gradient.addColorStop(1, qrCodeSettings.borderGradientEnd || "#2d2e83");

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, borderCanvas.width, borderCanvas.height);

  const borderThickness = 0.3 * scale;
  const t = borderThickness * (dpi / 2.54);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, borderCanvas.width, t);
  ctx.fillRect(0, borderCanvas.height - t, borderCanvas.width, t);
  ctx.fillRect(0, 0, t, borderCanvas.height);
  ctx.fillRect(borderCanvas.width - t, 0, t, borderCanvas.height);

  const borderDataURL = borderCanvas.toDataURL("image/png");

  const pdf = new jsPDF({ unit: "cm", format: [pageW, pageH] });
  pdf.addImage(
    borderDataURL,
    "PNG",
    safeMargin,
    safeMargin,
    pageW - 2 * safeMargin,
    pageH - 2 * safeMargin
  );

  const centerX = pageW / 2;
  let y = safeMargin + 1 * scale;

  const drawRest = () => {
    // --- Title ---
    if (qrTitle) {
      pdf.setFont("helvetica", "bold");
      let fontSize = (qrTitleSize || 18) * scale;
      fontSize = Math.max(fontSize, 10);
      pdf.setFontSize(fontSize);
      pdf.setTextColor(qrTitleColor || "#2d2e83");

      const maxWidth = pageW - 2.5 * safeMargin;
      const titleLines = pdf.splitTextToSize(qrTitle, maxWidth);
      pdf.text(titleLines, centerX, y, { align: "center" });

      const lineSpacing = fontSize * 0.02;
      const logoMargin = qrCodeSettings.showHolderLogo ? 1.0 * scale : 0.5 * scale;
      y += titleLines.length * (fontSize * 0.04 + lineSpacing) + logoMargin;
    }

    // --- Description ---
    if (qrDesc) {
      pdf.setFont("helvetica", "normal");
      let fontSize = (qrDescSize || 13) * scale;
      fontSize = Math.max(fontSize, 8);
      pdf.setFontSize(fontSize);
      pdf.setTextColor(qrDescColor || "#666");

      const maxWidth = pageW - 2.5 * safeMargin;
      const descLines = pdf.splitTextToSize(qrDesc, maxWidth);
      pdf.text(descLines, centerX, y, { align: "center" });

      const lineSpacing = fontSize * 0.02;
      // ⬆️ reduced margin so description sits higher
      y += descLines.length * (fontSize * 0.04 + lineSpacing) + 0.1 * scale;
    }

    // --- QR Container ---
    const borderWidthCm = 0.132 * scale;
    const paddingCm = 0.2 * scale;
    const radiusCm = 0.317 * scale;
    const availableHeight = pageH - y - footerH - safeMargin;

    let qrContainerCm;
    if (heightIn <= 3) {
      qrContainerCm = Math.min(4.2, availableHeight * 0.55, pageW - 2 * safeMargin);
    } else if (heightIn <= 4) {
      qrContainerCm = Math.min(6.0, availableHeight * 0.9, pageW - 2 * safeMargin);
    } else if (heightIn <= 6) {
      // ⬆️ bigger QR for 4×6
      qrContainerCm = Math.min(7.2, availableHeight * 0.75, pageW - 2 * safeMargin);
    } else {
      qrContainerCm = Math.min(7.5, availableHeight * 0.78, pageW - 2 * safeMargin);
    }

    const innerCm = qrContainerCm - 2 * borderWidthCm;
    const qrCm = innerCm - 2 * paddingCm;

    let scanMeH = 1.2 * scale;
    const footerY = pageH - footerH - safeMargin;
    const minRequired = qrContainerCm + scanMeH + 0.1 * scale;
    const showScanMe = availableHeight >= minRequired;
    if (!showScanMe) scanMeH = 0;

    const qrContainerY = y + (availableHeight - (qrContainerCm + scanMeH)) / 2 - 0.6 * scale;
    const qrContainerX = safeMargin + (pageW - 2 * safeMargin - qrContainerCm) / 2;

    // Outer rect
    pdf.setFillColor(qrTitleColor || "#2d2e83");
    pdf.roundedRect(qrContainerX, qrContainerY, qrContainerCm, qrContainerCm, radiusCm, radiusCm, "F");

    // Inner rect
    pdf.setFillColor("#fff");
    pdf.roundedRect(
      qrContainerX + borderWidthCm,
      qrContainerY + borderWidthCm,
      innerCm,
      innerCm,
      radiusCm - borderWidthCm,
      radiusCm - borderWidthCm,
      "F"
    );

    const qrPixel = Math.round((qrCm / 2.54) * dpi);
    generateQRCanvas((canvas) => {
      const qrDataURL = canvas.toDataURL("image/png");
      pdf.addImage(
        qrDataURL,
        "PNG",
        qrContainerX + borderWidthCm + paddingCm,
        qrContainerY + borderWidthCm + paddingCm,
        qrCm,
        qrCm
      );

      // --- Scan Me ---
      if (showScanMe) {
        const scanY = qrContainerY + qrContainerCm + 0.7 * scale;
        let scanFontSize = pageSize === "4-6" ? 16 : 16 * scale;
        scanFontSize = Math.max(scanFontSize, 10);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(scanFontSize);
        pdf.setTextColor("#060747ff");
        pdf.text("Scan Me", centerX, scanY, { align: "center" });
      }

      // --- Footer Strip ---
      pdf.setFillColor("#2d2e83");
      pdf.rect(safeMargin, footerY, pageW - 2 * safeMargin, footerH, "F");

      // Footer Branding
      pdf.setTextColor("#fff");

      let footerTitleFontSize = Math.min(Math.max(11 * scale, 9), 16);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(footerTitleFontSize);
      pdf.text("Powered by NetShine", centerX, footerY + 1.05 * scale, { align: "center" });

      let footerDetailFontSize = Math.min(Math.max(9 * scale, 8), 14);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(footerDetailFontSize);
      pdf.text(
        "info@netshinetech.com | www.netshineoffice.com",
        centerX,
        footerY + 1.65 * scale,
        { align: "center" }
      );

      pdf.text("Phone: +91 8264706203", centerX, footerY + 2.25 * scale, { align: "center" });

      const fileName = `NetShine-QR-${widthIn}x${heightIn}.pdf`;
      pdf.save(fileName);
    }, qrPixel);
  };

  // --- Header Logo ---
  if (qrCodeSettings.showHolderLogo) {
    const logoWidthCm = (qrCodeSettings.headerLogoWidth || headerLogoWidth || 250) * pxToCm * scale;
    const logoHeightCm = (qrCodeSettings.headerLogoHeight || headerLogoHeight || 150) * pxToCm * scale;

    const headerLogoImg = new Image();
    headerLogoImg.crossOrigin = "anonymous";
    headerLogoImg.src = qrCodeSettings.headerLogo || defaultLogoSrc;

    headerLogoImg.onload = () => {
      pdf.addImage(headerLogoImg, "PNG", (pageW - logoWidthCm) / 2, y, logoWidthCm, logoHeightCm);
      y += logoHeightCm + 1.0 * scale;
      drawRest();
    };

    headerLogoImg.onerror = () => {
      notify("error", "Failed to load header logo");
      drawRest();
    };
  } else {
    drawRest();
  }
};







const downloadQRPNG = (
  qrTitle,
  qrTitleColor,
  qrTitleSize,
  qrDesc,
  qrDescColor,
  qrDescSize,
  headerLogoWidth,
  headerLogoHeight
) => {
  const pageSize = qrCodeSettings.pageSize || "4-6";
  const [widthIn, heightIn] = pageSize.split("-").map(Number);
  const baseWidthIn = 4;
  const baseHeightIn = 6;
  const scale = Math.min(widthIn / baseWidthIn, heightIn / baseHeightIn);
  const pageW = widthIn * 2.54; // cm
  const pageH = heightIn * 2.54; // cm
  const dpi = 300;
  const cmToPx = dpi / 2.54;
  const ptToPx = dpi / 72;
  const pageWPx = Math.round(pageW * cmToPx);
  const pageHPx = Math.round(pageH * cmToPx);
  // Safe margin inside border
  const safeMargin = qrCodeSettings.borderless ? 0 : 0.25; // cm
  // Footer strip height
  const footerH = 2.5 * scale;
  const canvas = document.createElement("canvas");
  canvas.width = pageWPx;
  canvas.height = pageHPx;
  const ctx = canvas.getContext("2d");
  // --- Gradient border background ---
  const gradient = ctx.createLinearGradient(0, 0, pageWPx, pageHPx);
  gradient.addColorStop(0, qrCodeSettings.borderGradientStart || "#2d2e83");
  gradient.addColorStop(1, qrCodeSettings.borderGradientEnd || "#2d2e83");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, pageWPx, pageHPx);
  const borderThickness = 0.3 * scale;
  const t = borderThickness * cmToPx;
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, pageWPx, t);
  ctx.fillRect(0, pageHPx - t, pageWPx, t);
  ctx.fillRect(0, 0, t, pageHPx);
  ctx.fillRect(pageWPx - t, 0, t, pageHPx);
  const centerX = pageW / 2;
  let y = safeMargin + 1 * scale;
  const splitTextToSize = (text, maxWidthCm, fontSizePt, fontWeight = "normal") => {
    const fontSizePx = fontSizePt * ptToPx;
    ctx.font = `${fontWeight} ${fontSizePx}px Helvetica, sans-serif`;
    const maxWidthPx = maxWidthCm * cmToPx;
    const words = text.split(" ");
    const lines = [];
    let currentLine = words[0] || "";
    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = ctx.measureText(currentLine + " " + word).width;
      if (width < maxWidthPx) {
        currentLine += " " + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  };
  const drawRoundedRect = (x, y, w, h, r, fillStyle) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.arc(x + w - r, y + r, r, -Math.PI / 2, 0);
    ctx.lineTo(x + w, y + h - r);
    ctx.arc(x + w - r, y + h - r, r, 0, Math.PI / 2);
    ctx.lineTo(x + r, y + h);
    ctx.arc(x + r, y + h - r, r, Math.PI / 2, Math.PI);
    ctx.lineTo(x, y + r);
    ctx.arc(x + r, y + r, r, Math.PI, (3 * Math.PI) / 2);
    ctx.closePath();
    ctx.fillStyle = fillStyle;
    ctx.fill();
  };
  const drawRest = () => {
    // --- Title ---
    if (qrTitle) {
      let fontSize = (qrTitleSize || 18) * scale;
      fontSize = Math.max(fontSize, 10);
      const fontSizePx = fontSize * ptToPx;
      ctx.font = `bold ${fontSizePx}px Helvetica, sans-serif`;
      ctx.fillStyle = qrTitleColor || "#2d2e83";
      ctx.textAlign = "center";
      const maxWidth = pageW - 2.5 * safeMargin;
      const titleLines = splitTextToSize(qrTitle, maxWidth, fontSize, "bold");
      const lineSpacing = fontSize * 0.02;
      const perLineCm = fontSize * 0.04 + lineSpacing;
      const yPx = y * cmToPx;
      for (let i = 0; i < titleLines.length; i++) {
        ctx.fillText(titleLines[i], centerX * cmToPx, yPx + i * (perLineCm * cmToPx));
      }
      const logoMargin = qrCodeSettings.showHolderLogo ? 1.0 * scale : 0.5 * scale;
      y += titleLines.length * perLineCm + logoMargin;
    }
    // --- Description ---
    if (qrDesc) {
      let fontSize = (qrDescSize || 13) * scale;
      fontSize = Math.max(fontSize, 8);
      const fontSizePx = fontSize * ptToPx;
      ctx.font = `normal ${fontSizePx}px Helvetica, sans-serif`;
      ctx.fillStyle = qrDescColor || "#666";
      ctx.textAlign = "center";
      const maxWidth = pageW - 2.5 * safeMargin;
      const descLines = splitTextToSize(qrDesc, maxWidth, fontSize, "normal");
      const lineSpacing = fontSize * 0.02;
      const perLineCm = fontSize * 0.04 + lineSpacing;
      const yPx = y * cmToPx;
      for (let i = 0; i < descLines.length; i++) {
        ctx.fillText(descLines[i], centerX * cmToPx, yPx + i * (perLineCm * cmToPx));
      }
      // ⬆️ reduced margin so description sits higher
      y += descLines.length * perLineCm + 0.1 * scale;
    }
    // --- QR Container ---
    const borderWidthCm = 0.132 * scale;
    const paddingCm = 0.2 * scale;
    const radiusCm = 0.317 * scale;
    const availableHeight = pageH - y - footerH - safeMargin;
    let qrContainerCm;
    if (heightIn <= 3) {
      qrContainerCm = Math.min(4.2, availableHeight * 0.55, pageW - 2 * safeMargin);
    } else if (heightIn <= 4) {
      qrContainerCm = Math.min(6.0, availableHeight * 0.9, pageW - 2 * safeMargin);
    } else if (heightIn <= 6) {
      // ⬆️ bigger QR for 4×6
      qrContainerCm = Math.min(7.2, availableHeight * 0.75, pageW - 2 * safeMargin);
    } else {
      qrContainerCm = Math.min(7.5, availableHeight * 0.78, pageW - 2 * safeMargin);
    }
    const innerCm = qrContainerCm - 2 * borderWidthCm;
    const qrCm = innerCm - 2 * paddingCm;
    let scanMeH = 1.2 * scale;
    const footerY = pageH - footerH - safeMargin;
    const minRequired = qrContainerCm + scanMeH + 0.1 * scale;
    const showScanMe = availableHeight >= minRequired;
    if (!showScanMe) scanMeH = 0;
    const qrContainerY = y + (availableHeight - (qrContainerCm + scanMeH)) / 2 - 0.6 * scale;
    const qrContainerX = safeMargin + (pageW - 2 * safeMargin - qrContainerCm) / 2;
    // Outer rect
    drawRoundedRect(
      qrContainerX * cmToPx,
      qrContainerY * cmToPx,
      qrContainerCm * cmToPx,
      qrContainerCm * cmToPx,
      radiusCm * cmToPx,
      qrTitleColor || "#2d2e83"
    );
    // Inner rect
    drawRoundedRect(
      (qrContainerX + borderWidthCm) * cmToPx,
      (qrContainerY + borderWidthCm) * cmToPx,
      innerCm * cmToPx,
      innerCm * cmToPx,
      (radiusCm - borderWidthCm) * cmToPx,
      "#fff"
    );
    const qrPixel = Math.round((qrCm / 2.54) * dpi);
    generateQRCanvas((qrCanvas) => {
      const qrDataURL = qrCanvas.toDataURL("image/png");
      const qrImg = new Image();
      qrImg.src = qrDataURL;
      qrImg.onload = () => {
        ctx.drawImage(
          qrImg,
          (qrContainerX + borderWidthCm + paddingCm) * cmToPx,
          (qrContainerY + borderWidthCm + paddingCm) * cmToPx,
          qrCm * cmToPx,
          qrCm * cmToPx
        );
        // --- Scan Me ---
        if (showScanMe) {
          const scanY = qrContainerY + qrContainerCm + 0.7 * scale;
          let scanFontSize = pageSize === "4-6" ? 16 : 16 * scale;
          scanFontSize = Math.max(scanFontSize, 10);
          const scanFontSizePx = scanFontSize * ptToPx;
          ctx.font = `bold ${scanFontSizePx}px Helvetica, sans-serif`;
          ctx.fillStyle = "#060747ff";
          ctx.textAlign = "center";
          ctx.fillText("Scan Me", centerX * cmToPx, scanY * cmToPx);
        }
        // --- Footer Strip ---
        ctx.fillStyle = "#2d2e83";
        ctx.fillRect(safeMargin * cmToPx, footerY * cmToPx, (pageW - 2 * safeMargin) * cmToPx, footerH * cmToPx);
        // Footer Branding
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        let footerTitleFontSize = Math.min(Math.max(11 * scale, 9), 16);
        const footerTitleFontSizePx = footerTitleFontSize * ptToPx;
        ctx.font = `bold ${footerTitleFontSizePx}px Helvetica, sans-serif`;
        ctx.fillText("Powered by NetShine", centerX * cmToPx, (footerY + 1.05 * scale) * cmToPx);
        let footerDetailFontSize = Math.min(Math.max(9 * scale, 8), 14);
        const footerDetailFontSizePx = footerDetailFontSize * ptToPx;
        ctx.font = `normal ${footerDetailFontSizePx}px Helvetica, sans-serif`;
        ctx.fillText(
          "info@netshinetech.com | www.netshineoffice.com",
          centerX * cmToPx,
          (footerY + 1.65 * scale) * cmToPx
        );
        ctx.fillText("📞 +91 8264706203", centerX * cmToPx, (footerY + 2.25 * scale) * cmToPx);
        // ✅ Save file with page size suffix
        const fileName = `NetShine-QR-${widthIn}x${heightIn}.png`;
        const link = document.createElement("a");
        link.download = fileName;
        link.href = canvas.toDataURL("image/png");
        link.click();
      };
    }, qrPixel);
  };
  // --- Header Logo ---
  if (qrCodeSettings.showHolderLogo) {
    const logoWidthCm = (qrCodeSettings.headerLogoWidth || headerLogoWidth || 250) * (2.54 / 96) * scale;
    const logoHeightCm = (qrCodeSettings.headerLogoHeight || headerLogoHeight || 150) * (2.54 / 96) * scale;
    const headerLogoImg = new Image();
    headerLogoImg.crossOrigin = "anonymous";
    headerLogoImg.src = qrCodeSettings.headerLogo || defaultLogoSrc;
    headerLogoImg.onload = () => {
      ctx.drawImage(headerLogoImg, ((pageW - logoWidthCm) / 2) * cmToPx, y * cmToPx, logoWidthCm * cmToPx, logoHeightCm * cmToPx);
      y += logoHeightCm + 1.0 * scale;
      drawRest();
    };
    headerLogoImg.onerror = () => {
      notify("error", "Failed to load header logo");
      drawRest();
    };
  } else {
    drawRest();
  }
};
  const saveSurvey = async () => {
    const payload = {
      title: editingSurvey?.title || "",
      description: editingSurvey?.description || "",
      status: editingSurvey?.status || "Draft",
      questions: questions || [],
      branding: branding || {},
      settings: settings || {},
      qrcode_settings: qrCodeSettings || {},
      conditional_logic: conditionalLogic || {}, // ✅ always here now
      sharing: { key: editingSurvey?.key || `survey-${Date.now()}` },
    };
    try {
      setLoading(true);
      let isNew = !editingSurvey?.surveyid;
      if (!isNew) {
        await axios.put(`${apiUrl}/feedback/surveys/${editingSurvey.surveyid}`, payload, authHeaders());
        notify("success", "Survey updated successfully.");
      } else {
        const res = await axios.post(`${apiUrl}/feedback/surveys`, payload, authHeaders());
        if (res.data) {
          notify("success", "Survey created successfully.");
          setEditingSurvey({ ...editingSurvey, surveyid: res.data.surveyid });
          updateSharing({ ...editingSurvey, surveyid: res.data.surveyid });
        }
      }
      await fetchSurveys();
      initialConfigRef.current = JSON.stringify(payload);
      if (isNew) stopEditing(); // Land to main page after creation
    } catch (err) {
      console.error("Error saving survey:", err);
      notify("error", "Failed to save survey.");
    } finally {
      setLoading(false);
    }
  };
  const confirmActionHandler = (action, data) => {
    setConfirmAction(action);
    setConfirmData(data);
    setShowConfirmModal(true);
  };
  const handleConfirm = async () => {
    if (confirmAction === "delete") {
      await deleteSurvey(confirmData.id || confirmData.surveyid);
      notify("success", "Survey deleted successfully.");
    } else if (confirmAction === "duplicate") {
      await duplicateSurvey(confirmData);
      notify("success", "Survey duplicated successfully.");
    }
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };
  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };
  const deleteSurvey = async (id) => {
    try {
      setLoading(true);
      const survey = surveys.find((s) => s.id === id || s.surveyid === id);
      const surveyid = survey?.surveyid || id;
      await axios.delete(`${apiUrl}/feedback/surveys/${surveyid}`, authHeaders());
      setSurveys((prev) => prev.filter((s) => s.surveyid !== surveyid && s.id !== id));
    } catch (err) {
      console.error("Error deleting survey:", err);
      notify("error", "Failed to delete survey.");
    } finally {
      setLoading(false);
    }
  };
  const duplicateSurvey = async (survey) => {
    try {
      setLoading(true);
      const payload = {
        title: `${survey.title} (Copy)`,
        description: survey.description || "",
        status: "Draft",
        questions: survey.questions || [],
        branding: survey.branding || {},
        settings: survey.settings || {},
        qrcode_settings: survey.qrCodeSettings || qrCodeSettings,
        conditional_logic: survey.conditionalLogic || conditionalLogic,
        sharing: { key: `${survey.key || "copy"}-copy-${Date.now()}` },
      };
      await axios.post(`${apiUrl}/feedback/surveys`, payload, authHeaders());
      await fetchSurveys();
    } catch (err) {
      console.error("Error duplicating survey:", err);
      notify("error", "Failed to duplicate survey.");
    } finally {
      setLoading(false);
    }
  };
  const addQuestion = (type) => {
    const base = { id: Date.now(), type, label: "", required: false, placeholder: "" };
    if (type === "rating")         base.style = "star";
    if (type === "section_header") base.label = "Section Heading";
    if (["dropdown","radio","checkbox"].includes(type))
      base.options = ["Option A", "Option B", "Option C"];
    setQuestions([...questions, base]);
  };
  const updateQuestion = (id, field, value) => {
    setQuestions(questions.map((q) => (q.id === id ? { ...q, [field]: value } : q)));
  };
  const removeQuestion = (id) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(questions);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setQuestions(reordered);
  };
  const renderQuestionPreview = (q) => {
    const inputStyle = { width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #dee2e6", background: "#f8f9fa", color: "#6c757d", fontSize: "0.9rem" };
    switch (q.type) {
      case "section_header":
        return <div style={{ fontWeight: 700, fontSize: "1rem", color: branding?.themeColor || "#0d6efd", borderBottom: `2px solid ${branding?.themeColor || "#0d6efd"}`, paddingBottom: 4 }}>{q.label}</div>;
      case "rating":
        return q.style === "star"
          ? <div style={{ fontSize: "1.4rem", letterSpacing: 4 }}>⭐ ⭐ ⭐ ⭐ ⭐</div>
          : <div style={{ fontSize: "1.4rem", letterSpacing: 4 }}>😞 😟 😐 🙂 😄</div>;
      case "nps":
        return (
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
            {[...Array(11)].map((_, i) => (
              <button key={i} style={{ width: 30, height: 30, borderRadius: 4, border: "1px solid #ddd",
                background: i === 5 ? (branding?.themeColor || "#0d6efd") : "#fff",
                color: i === 5 ? "#fff" : "#000", fontSize: "0.78rem" }}>
                {i}
              </button>
            ))}
          </div>
        );
      case "text":
        return <input type="text" disabled placeholder={q.placeholder || "Short answer…"} style={inputStyle} />;
      case "textarea":
        return <textarea disabled rows={3} placeholder={q.placeholder || "Long answer…"} style={{ ...inputStyle, resize: "none" }} />;
      case "email":
        return <input type="email" disabled placeholder={q.placeholder || "your@email.com"} style={inputStyle} />;
      case "phone":
        return <input type="tel" disabled placeholder={q.placeholder || "+91 98765 43210"} style={inputStyle} />;
      case "number":
        return <input type="number" disabled placeholder={q.placeholder || "0"} style={inputStyle} />;
      case "date":
        return <input type="date" disabled style={inputStyle} />;
      case "dropdown":
        return (
          <select disabled style={{ ...inputStyle, appearance: "none" }}>
            <option>Select an option…</option>
            {(q.options || []).map((opt, i) => <option key={i}>{opt}</option>)}
          </select>
        );
      case "radio":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(q.options || ["Option A", "Option B"]).map((opt, i) => (
              <label key={i} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "default" }}>
                <input type="radio" disabled style={{ accentColor: branding?.themeColor || "#0d6efd" }} />
                <span style={{ fontSize: "0.88rem" }}>{opt}</span>
              </label>
            ))}
          </div>
        );
      case "checkbox":
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {(q.options || ["Option A", "Option B"]).map((opt, i) => (
              <label key={i} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "default" }}>
                <input type="checkbox" disabled style={{ accentColor: branding?.themeColor || "#0d6efd" }} />
                <span style={{ fontSize: "0.88rem" }}>{opt}</span>
              </label>
            ))}
          </div>
        );
      // legacy alias
      case "multiple":
        return (
          <select disabled style={{ ...inputStyle, appearance: "none" }}>
            <option>Option 1</option><option>Option 2</option>
          </select>
        );
      default:
        return null;
    }
  };
  const handleLogoUrlChange = (e) => setBranding({ ...branding, logoSrc: e.target.value });
  const handleBackgroundChange = (e) => setBranding({ ...branding, background: e.target.value });
  const handleBackgroundValueChange = (value) => setBranding({ ...branding, backgroundValue: value });
  const getBackgroundStyle = () =>
    branding.background === "gradient"
      ? "linear-gradient(to right, #f0f0f0, #ffffff)"
      : branding.background === "image"
      ? `url(${branding.backgroundValue})`
      : branding.backgroundValue;
  const handleColorChange = (field, color) => setBranding({ ...branding, [field]: color.hex });
  const handleTitleFontSizeChange = (e) => setBranding({ ...branding, titleFontSize: parseFloat(e.target.value) });
  const handleConditionalChange = (field, value) =>
    setConditionalLogic({
      ...conditionalLogic,
      [field]: value,
    });
  const handleQRSettingChange = (field, value) => {
    setQrCodeSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  const saveGlobalSettings = async () => {
    try {
      setLoading(true);
      await axios.put(`${apiUrl}/feedback/settings`, globalSettings, authHeaders());
      notify("success", "Global settings saved successfully.");
    } catch (err) {
      console.error("Error saving global settings:", err);
      notify("error", "Failed to save global settings.");
    } finally {
      setLoading(false);
    }
  };
  const handleUsePreviewTemplate = (template) => {
    loadTemplate(template);
    setPreviewTemplate(null);
  };
  const isSaved = JSON.stringify({ questions, branding, settings, qrCodeSettings }) === initialConfigRef.current;
  return (
    <div className="page-wrapper-full">
      <div className="content container-fluid">
        <div className="page-header page-top-icons" aria-label="Page Header">
          <div className="row align-items-center">
            <div className="col-md-12 d-flex justify-content-between align-items-center">
              <h3 className="fw-bold text-dark mb-0">
                <i className="fas fa-wpforms text-primary me-2" aria-hidden="true"></i> Forms &amp; Surveys
              </h3>
              <div className="d-flex gap-2">
                {editingSurvey && (
                  <Button
                    variant="secondary"
                    onClick={stopEditing}
                    className="px-4 py-2"
                    aria-label="Cancel Editing"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={() => (editingSurvey ? saveSurvey() : startEditing())}
                  className="px-4 py-2"
                  aria-label={editingSurvey ? "Save Survey" : "Create New Survey"}
                  disabled={loading}
                >
                  {editingSurvey ? "Save" : "+ Create New Survey"}
                </Button>
              </div>
            </div>
          </div>
        </div>
        {!editingSurvey ? (
          loading ? (
            <div>Loading surveys...</div>
          ) : Array.isArray(surveys) ? (
            <SurveyList
              surveys={surveys}
              startEditing={startEditing}
              confirmActionHandler={confirmActionHandler}
              loading={loading}
            />
          ) : (
            <div>No surveys available</div>
          )
        ) : (
          <SurveyEditor
            editingSurvey={editingSurvey}
            setEditingSurvey={setEditingSurvey}
            questions={questions}
            setQuestions={setQuestions}
            branding={branding}
            setBranding={setBranding}
            settings={settings}
            setSettings={setSettings}
            globalSettings={globalSettings}
            setGlobalSettings={setGlobalSettings}
            qrCodeSettings={qrCodeSettings}
            setQrCodeSettings={setQrCodeSettings}
            conditionalLogic={conditionalLogic}
            setConditionalLogic={setConditionalLogic}
            surveyLink={surveyLink}
            saveSurvey={saveSurvey}
            stopEditing={stopEditing}
            fetchGlobalSettings={fetchGlobalSettings}
            saveGlobalSettings={saveGlobalSettings}
            addQuestion={addQuestion}
            updateQuestion={updateQuestion}
            removeQuestion={removeQuestion}
            onDragEnd={onDragEnd}
            renderQuestionPreview={renderQuestionPreview}
            handleLogoUrlChange={handleLogoUrlChange}
            handleBackgroundChange={handleBackgroundChange}
            handleBackgroundValueChange={handleBackgroundValueChange}
            handleColorChange={handleColorChange}
            handleTitleFontSizeChange={handleTitleFontSizeChange}
            handleConditionalChange={handleConditionalChange}
            handleQRSettingChange={handleQRSettingChange}
            previewDevice={previewDevice}
            setPreviewDevice={setPreviewDevice}
            getBackgroundStyle={getBackgroundStyle}
            isDownloadOpen={isDownloadOpen}
            setIsDownloadOpen={setIsDownloadOpen}
            downloadQRPDF={downloadQRPDF}
            downloadQRPNG={downloadQRPNG}
            generateQRCanvas={generateQRCanvas}
            activeKey={activeKey}
            onSelect={(key) => setActiveKey(key)}
          />
        )}
        <Modal
          show={showConfirmModal}
          onHide={handleCancelConfirm}
          centered
          aria-label="Confirmation Modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirm Action</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {confirmAction === "delete" && (
              <p>Are you sure you want to delete the survey "{confirmData?.title}"?</p>
            )}
            {confirmAction === "duplicate" && (
              <p>Are you sure you want to duplicate the survey "{confirmData?.title}"?</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCancelConfirm}>
              No
            </Button>
            <Button variant="primary" onClick={handleConfirm}>
              Yes
            </Button>
          </Modal.Footer>
        </Modal>
        {/* ═══════════════════════════════════════════════════════════════════
            TEMPLATE LIBRARY — full-screen right overlay (no Bootstrap Modal)
        ═══════════════════════════════════════════════════════════════════ */}
        {showTemplateModal && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 1055,
            display: "flex", alignItems: "stretch",
          }}>
            {/* Dark backdrop — click to close */}
            <div
              onClick={() => setShowTemplateModal(false)}
              style={{ flex: 1, background: "rgba(0,0,0,0.45)", cursor: "pointer" }}
            />
            {/* Panel — 60% of screen, right side, full height */}
            <div style={{
              width: "60vw", flexShrink: 0,
              height: "100vh", overflowY: "hidden",
              background: "#fff",
              boxShadow: "-8px 0 48px rgba(0,0,0,0.18)",
              display: "flex", flexDirection: "column",
            }}>
              <TemplateLibrary
                templates={templates}
                onUse={loadTemplate}
                onBlank={loadBlank}
                onPreview={setPreviewTemplate}
                onClose={() => setShowTemplateModal(false)}
              />
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            TEMPLATE PREVIEW — full-screen right overlay
        ═══════════════════════════════════════════════════════════════════ */}
        {previewTemplate && (
          <div style={{
            position: "fixed", inset: 0, zIndex: 1060,
            display: "flex", alignItems: "stretch",
          }}>
            {/* Backdrop */}
            <div
              onClick={() => setPreviewTemplate(null)}
              style={{ flex: 1, background: "rgba(0,0,0,0.45)", cursor: "pointer" }}
            />
            {/* Panel */}
            <div style={{
              width: "60vw", flexShrink: 0,
              height: "100vh", overflowY: "hidden",
              background: "#fff",
              boxShadow: "-8px 0 48px rgba(0,0,0,0.18)",
              display: "flex", flexDirection: "column",
            }}>
              <TemplatePreview
                template={previewTemplate}
                onUse={() => handleUsePreviewTemplate(previewTemplate)}
                onClose={() => setPreviewTemplate(null)}
              />
            </div>
          </div>
        )}
      </div>
      <style>{`
        .survey-card {
          transition: all 0.25s ease;
        }
        .survey-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
          border-left: 4px solid var(--bs-primary);
        }
        .template-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
          border-left: 4px solid #007bff;
        }
        .template-blank-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
          background: #eef6ff;
        }
        .template-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 1.5rem;
        }
        .template-card,
        .template-blank-card {
          border-radius: 12px;
          transition: all 0.3s ease;
          cursor: pointer;
          background: #fff;
        }
        .modal-right .modal-dialog {
          position: fixed !important;
          top: 0 !important;
          right: 0 !important;
          margin: 0 !important;
          height: 100vh !important;
          width: 60vw !important;
          max-width: 60vw !important;
          transform: none !important;
          display: flex !important;
          flex-direction: column !important;
        }
        .modal-right .modal-content {
          height: 100vh !important;
          max-height: 100vh !important;
          border-radius: 0 !important;
          border: none !important;
          display: flex !important;
          flex-direction: column !important;
          overflow: hidden !important;
          box-shadow: -8px 0 40px rgba(0,0,0,0.14) !important;
        }
        .modal-right .modal-header {
          display: none !important;
        }
        .modal-right .modal-body {
          padding: 0 !important;
          flex: 1 !important;
          overflow: hidden !important;
          display: flex !important;
          flex-direction: column !important;
        }
        .modal-right .modal-footer {
          display: none !important;
        }
        .survey-card .btn-link {
          padding: 0.25rem 0.5rem;
          font-size: 1.1rem;
        }
        .survey-card .btn-link:hover {
          text-decoration: none;ss
          transform: scale(1.15);
          transition: transform 0.15s ease;
        }
        .survey-card .card-footer {
          border-top: 1px solid #f0f0f0;
        }
      `}</style>
    </div>
  );
};

export default ManageSurveys;